import { defaults, defaultsDeep } from 'lodash'
import React, { useCallback, useContext, useMemo } from 'react'
import { useCounter } from 'ahooks'
import type { TrackEventParams, TrackBasicParams } from '@/services/track'
import { track } from '@/services/track'
import { useSelector } from '@/store'
import type { LoadableComponent } from '@loadable/component'
/**
 * 场景映射
 */
export const TRACK_STAGE = {
  orderCreate: '创建订单',
  orderEdit: '编辑订单',
  orderCopy: '复制订单',
}

const ADDRESS_TYPE = {
  departure: '发货地址',
  arrival: '收货地址',
}

// 页面高阶

interface PageContext {
  /** 页面 */
  page?: keyof typeof TRACK_STAGE
  /** 统计 */
  count: number
  /** 执行统计 */
  incCount: () => void
  /** 订单唯一标识 */
  orderSign: string
}

const pageContext = React.createContext<PageContext>(null)
export const withAddressSplitAlgorithmTrack = <P extends Record<string, any>>(
  Component: React.FC<P> | LoadableComponent<P>,
  page: keyof typeof TRACK_STAGE | ((props: P) => keyof typeof TRACK_STAGE)
) => {
  const Provider = pageContext.Provider

  const AddressSplitAlgorithmTrackPage: React.FC<P> = (props) => {
    const [count, { inc: incCount }] = useCounter()
    const seller_id = useSelector((state) => state.auth.userInfo?.sellerId)
    const orderSign = useMemo(() => seller_id + (Date.now() + Math.random() * 10e9).toString(32), [seller_id])
    const finalPage = typeof page === 'string' ? page : typeof page === 'function' ? page(props) : undefined

    return (
      <Provider value={{ count, incCount, page: finalPage, orderSign }}>
        <Component {...props} />
      </Provider>
    )
  }

  AddressSplitAlgorithmTrackPage.displayName = 'AddressSplitAlgorithmTrackPage'
  return AddressSplitAlgorithmTrackPage
}

// 组件高阶

interface ComponentContext extends Pick<PageContext, 'page'> {
  /** 页面 */
  page?: keyof typeof TRACK_STAGE
  /** 地址类型 */
  type: 'departure' | 'arrival'
  /** 国家 */
  countryCode?: string | (() => string)
}

const componentContext = React.createContext<ComponentContext>(null)
export interface AddressSplitAlgorithmTrackProviderProps extends ComponentContext {
  children: React.ReactElement
}

export const useAddressSplitAlgorithmContext = () => {
  const page = useContext(pageContext)
  const comp = useContext(componentContext)
  const context = useMemo(() => defaults({}, page, comp), [comp, page])
  const metadata = useMemo<Record<string, any>>(() => context, [context])
  return { context, metadata }
}
/** 地址拆分算法统计 */
export const AddressSplitAlgorithmTrackProvider: React.FC<AddressSplitAlgorithmTrackProviderProps> = (() => {
  const Provider = componentContext.Provider
  return (props) => {
    const { page, type, countryCode, children } = props
    return <Provider value={{ page, type, countryCode }}>{children}</Provider>
  }
})()

/** 地址拆分算法埋点 */
export const useAddressSplitAlgorithm = () => {
  const { page, count = 0, incCount, orderSign } = useContext(pageContext) || {}
  const { page: cPage, countryCode, type } = useContext(componentContext) || {}
  const seller_id = useSelector((state) => state.auth.userInfo?.sellerId)
  const algorithmTrackData = useMemo(() => {
    return {
      /** 操作端类型 */
      aa_optype: 'PC',
      /** 调用来源 */
      aa_region: 'OW',
      /** 地址类型 */
      aa_address_type: ADDRESS_TYPE[type],
      /** 使用场景 */
      aa_stage: TRACK_STAGE[cPage || page],
      /** 第N次触发算法 */
      aa_times: count.toString(),
      /** 国家/地区 */
      aa_country: typeof countryCode === 'string' ? countryCode : '',
      /** 商家 ID */
      seller_id,
      /** 唯一 ID */
      aa_uid: seller_id,
      /** 订单标识 */
      aa_order_sign: orderSign,
    } as const
  }, [page, cPage, type, count, countryCode, seller_id, orderSign])

  type TrackAddressSplitAlgorithmParams = Omit<
    TrackEventParams['addressSplitAlgorithm'],
    'aa_order_sign' | keyof typeof orderCreateConnectAlgorithmTrackData | keyof TrackBasicParams
  >

  /** 地址拆分算法使用记录埋点 */
  const trackAddressSplitAlgorithm = useCallback(
    async (params: Partial<TrackAddressSplitAlgorithmParams>) => {
      const data: Partial<TrackEventParams['addressSplitAlgorithm']> = {
        ...algorithmTrackData,
        /** 国家/地区 */
        aa_country: typeof countryCode === 'function' ? countryCode() : undefined,
        ...params,
      }

      await track('addressSplitAlgorithm', defaultsDeep({}, data, algorithmTrackData))
      incCount()
    },
    [algorithmTrackData, countryCode, incCount]
  )

  const orderCreateConnectAlgorithmTrackData = useMemo(() => {
    return {
      /** 操作端类型 */
      aa_optype: 'PC',
      /** 调用来源 */
      aa_region: 'OW',
      /** 第N次触发算法 */
      aa_times: count.toString(),
      /** 商家 ID */
      seller_id,
      /** 唯一 ID */
      aa_uid: seller_id,
      /** 订单标识 */
      aa_order_sign: orderSign,
      /** 使用场景 */
      aa_stage: TRACK_STAGE[cPage || page],
    } as const
  }, [count, seller_id, orderSign, cPage, page])

  type TrackOrderCreateConnectAddressSplitAlgorithmParams = Omit<
    TrackEventParams['addressSplitAlgorithmOrderCreate'],
    keyof typeof orderCreateConnectAlgorithmTrackData | keyof TrackBasicParams
  >

  /** 拆分算法关联创单 */
  const trackOrderCreateConnectAddressSplitAlgorithm = useCallback(
    async (params: TrackOrderCreateConnectAddressSplitAlgorithmParams) => {
      const data: Partial<TrackEventParams['addressSplitAlgorithmOrderCreate']> = {
        ...orderCreateConnectAlgorithmTrackData,
        ...params,
      }

      await track('addressSplitAlgorithmOrderCreate', defaultsDeep({}, data, orderCreateConnectAlgorithmTrackData))
    },
    [orderCreateConnectAlgorithmTrackData]
  )

  return { trackAddressSplitAlgorithm, trackOrderCreateConnectAddressSplitAlgorithm }
}
